import { useMemo } from 'react';

import { useIsScribeEnableRtlEnabled } from '@/features/flags';
import {
  mapLocaleAliasToSupportedLocale,
  type SupportedLocale,
} from '@/utils/i18n/supportedLocales';

/**
 * Resolves the OS/browser locale (`navigator.language`) to a supported app
 * locale, or `undefined` when unavailable/unsupported.
 *
 * Used by pre-auth desktop surfaces that have no Next.js i18n routing
 * (`router.locale` is always `en` there) as a synchronous locale fallback, so
 * they render in the user's OS language on first paint instead of flashing
 * English while the saved language loads.
 */
export function useOsLocale(): SupportedLocale | undefined {
  const isRtlLocaleFeatureEnabled = useIsScribeEnableRtlEnabled();

  return useMemo(
    () =>
      typeof navigator !== 'undefined'
        ? mapLocaleAliasToSupportedLocale(navigator.language, {
            isRtlLocaleFeatureEnabled,
          })
        : undefined,
    [isRtlLocaleFeatureEnabled]
  );
}
